import {faInfoCircle} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {TFunction} from 'i18next';
import {Component} from 'react';

import {withTranslation, WithTranslation} from 'react-i18next';
import NumberFormat from 'react-number-format';
import {connect} from 'react-redux';
import {Dispatch} from 'redux';
import ReactTooltip from 'react-tooltip';
import {ApplicationState} from '../../../../store';
import {getKPIDataRequest, KpiTypes} from '../../../../store/analytics';
import {
    KeyPerformanceIndicatorHelper
} from '../../../../store/analytics/types/helpers/key-performance-indicator.helper';
import {
    KeyPerformanceIndicatorData,
    KeyPerformanceIndicatorRequest,
} from '../../../../store/analytics/types/key-performance-indicator.types';
import {CurrencyInfo} from '../../../../store/fleet-customers';
import {ControlsSnapshot} from '../../dashboard-controls/controls-snapshot';
import WidgetLoading from '../widget-loading';
import styles from './kpi-box.module.scss';
import {kpiMetaStore, NumberFormatter} from './kpi-meta-store';
import {buildRequest, isValidSnapshot} from './request-builder';

class KpiBox extends Component<AllProps> {
    public componentDidMount(): void {
        const {controlsSnapshot, dispatchKeyPerformanceIndicatorRequest, configuration, activeCurrency} = this.props;

        if (controlsSnapshot && isValidSnapshot(controlsSnapshot)) {
            dispatchKeyPerformanceIndicatorRequest(buildRequest(controlsSnapshot, configuration.type, activeCurrency));
        }
    }

    public componentDidUpdate(prevProps: Readonly<AllProps>): void {
        const {dispatchKeyPerformanceIndicatorRequest, controlsSnapshot, configuration, activeCurrency} = this.props;

        if (
            controlsSnapshot &&
            !controlsSnapshot.equals(prevProps.controlsSnapshot) &&
            isValidSnapshot(controlsSnapshot)
        ) {
            dispatchKeyPerformanceIndicatorRequest(buildRequest(controlsSnapshot, configuration.type, activeCurrency));
        }
    }

    public render(): JSX.Element {
        const {t, configuration, data, controlsSnapshot} = this.props;
        const keyPerformanceData = data.find((d) => d.type === configuration.type);
        if (keyPerformanceData) {
            KeyPerformanceIndicatorHelper.formatBigNumber(keyPerformanceData);
        }

        const formatter = this.getFormatter(configuration.type);

        const tooltipText = this.getTooltipTextForKPIType(configuration.type, t);

        return (
            <div className={styles.widgetContainer}>
                {keyPerformanceData ? (
                    <div className={styles.chartView}>
                        <div className={styles.titlePanel}>
                            {this.getTitle(configuration.type, controlsSnapshot, t).toUpperCase()}
                            {tooltipText ?
                                <>
                                    <div className={styles.infoIcon}
                                         data-tip={tooltipText}
                                         data-for={configuration.type}>
                                        <FontAwesomeIcon icon={faInfoCircle}/></div>
                                    <ReactTooltip className={styles.tooltip} html border effect="solid"
                                                  backgroundColor="#000000" id={configuration.type}/>
                                </>
                                : null}
                        </div>
                        <div className={styles.valuePanel}>
                            <div className={styles.value}>
                                {keyPerformanceData.value ? <>
                                    <NumberFormat
                                        value={keyPerformanceData.value}
                                        displayType="text"
                                        decimalSeparator={formatter.decimalSeparator}
                                        thousandSeparator={formatter.thousandsSeparator}
                                        fixedDecimalScale={formatter.fixedDecimalScale}
                                        decimalScale={formatter.decimalScale}
                                    />
                                    <span className={styles.unit}>{keyPerformanceData.unit}</span>
                                </> : ''}
                                {keyPerformanceData.stringValue ? keyPerformanceData.stringValue : ''}
                            </div>
                            <div className={styles.label}>
                                {this.getSubtitle(configuration.type, controlsSnapshot, t)}
                            </div>
                        </div>
                    </div>
                ) : (
                    <WidgetLoading/>
                )}
            </div>
        );
    }

    private getSubtitle(type: KpiTypes, controlsSnapshot: ControlsSnapshot, t: TFunction): string {
        return kpiMetaStore.get(type)!.subtitleFunction(controlsSnapshot, t);
    }

    private getTitle(type: KpiTypes, controlsSnapshot: ControlsSnapshot, t: TFunction): string {
        return kpiMetaStore.get(type)!.titleFunction(controlsSnapshot, t);
    }

    private getFormatter(type: KpiTypes): NumberFormatter {
        if (kpiMetaStore.has(type)) {
            return kpiMetaStore.get(type)!.numberFormatter;
        }
        return new NumberFormatter('.', ',', false, 0);
    }

    private getTooltipTextForKPIType(kpiType: KpiTypes, t: TFunction): string | undefined {
        switch (kpiType) {
            case KpiTypes.JobsAvgAcceptanceTime: {
                return t('The average acceptance time is the time elapsed between job submission and of purchase order creation.');
            }
            default:
                return '';
        }
    }
}

const mapStateToProps = ({analytics, authentication}: ApplicationState) => ({
    data: analytics.keyPerformanceIndicatorData,
    activeCurrency: authentication.fleetCustomer?.activeCurrency,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    dispatchKeyPerformanceIndicatorRequest: (requestBody: KeyPerformanceIndicatorRequest) =>
        dispatch(getKPIDataRequest(requestBody)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(KpiBox));

interface PropsFromState {
    data: KeyPerformanceIndicatorData[];
    activeCurrency: CurrencyInfo;
}

interface PropsFromDispatch {
    dispatchKeyPerformanceIndicatorRequest: typeof getKPIDataRequest;
}

export interface OwnProps {
    configuration: Configuration;
    controlsSnapshot: ControlsSnapshot;
}

type AllProps = OwnProps & WithTranslation & PropsFromState & PropsFromDispatch;

export interface Configuration {
    type: KpiTypes;
}
